import { createContext, useContext } from 'react';
import type { SurfaceContextValue, SurfaceProviderProps } from './types';

const SurfaceContext = createContext<SurfaceContextValue>({
  level: 'base',
  depth: 0,
});

/**
 * SurfaceProvider - Provides surface elevation and theme override context
 *
 * Allows nested components to know their elevation level and optionally
 * override the parent theme (e.g., photo viewer always light theme).
 *
 * @example
 * <SurfaceProvider level="surface1" theme="light">
 *   <PhotoViewer />
 * </SurfaceProvider>
 */
export function SurfaceProvider({ level, theme, children }: SurfaceProviderProps) {
  const parent = useContext(SurfaceContext);

  const value: SurfaceContextValue = {
    level,
    theme,
    depth: parent.depth + 1,
  };

  // Apply theme override if specified
  const className = theme === 'dark' ? 'dark' : theme === 'light' ? '' : undefined;

  return (
    <SurfaceContext.Provider value={value}>
      {className !== undefined ? (
        <div className={className || undefined}>{children}</div>
      ) : (
        <>{children}</>
      )}
    </SurfaceContext.Provider>
  );
}

/**
 * useSurface - Hook to access surface context
 *
 * Returns current surface elevation level and theme override.
 *
 * @example
 * function MyComponent() {
 *   const { level, theme, depth } = useSurface();
 *   return <div>Surface level: {level}, depth: {depth}</div>;
 * }
 */
export function useSurface(): SurfaceContextValue {
  return useContext(SurfaceContext);
}

export { SurfaceContext };
